import { AType, summon } from "@silversea-ssc/functional-support"
import { Semigroup } from "fp-ts/Semigroup"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import * as ORD from "fp-ts/Ord"
import * as N from "fp-ts/number"
import { PathReporter } from "io-ts/PathReporter"

const _Currency = summon((F) => F.keysOf({ EUR: null, USD: null, GBP: null, CAD: null, AUD: null }))
export type Currency = AType<typeof _Currency>
export const Currency = /* @__PURE__ */ Object.assign(_Currency, {
    buildUnsafe: (value: string): Currency =>
        F.pipe(
            _Currency.strictType.decode(value),
            E.getOrElse((e): Currency => {
                throw new Error(`Tried to unsafely build a non empty string: ${PathReporter.report(E.left(e))}`)
            }),
        ),
})

const _Amount = summon((F) =>
    F.interface(
        {
            value: F.number(),
            currency: Currency(F),
        },
        "Amount",
    ),
)

export type Amount = AType<typeof _Amount>

const sum = (first: Amount, second: Amount): Amount => ({
    value: first.value + second.value,
    currency: first.currency, //TODO what happen if first's and second's currency are different?
})

const subtract = (first: Amount, second: Amount): Amount => ({
    value: first.value - second.value,
    currency: first.currency, //TODO what happen if first's and second's currency are different?
})

const cheapest = F.pipe(N.Ord, ORD.contramap(_Amount.lensFromProp("value").get))
const value = _Amount.lensFromProp("value")

export const Amount = /* @__PURE__ */ Object.assign(_Amount, {
    multiplyBy:
        (multiplier: number) =>
        (amount: Amount): Amount => ({
            value: amount.value * multiplier,
            currency: amount.currency,
        }),
    divideBy:
        (divider: number) =>
        (amount: Amount): Amount => ({
            value: amount.value / divider,
            currency: amount.currency,
        }),
    sum,
    subtract,
    zero: (currency: Currency): Amount => _Amount.build({ value: 0, currency: currency }),
    isZero: (amount: Amount): boolean => amount.value === 0,
    isZeroOrNegative: (amount: Amount): boolean => amount.value <= 0,
    isNegative: (amount: Amount): boolean => amount.value < 0,
    isPositive: (amount: Amount): boolean => amount.value > 0,
    SemigroupSum: <Semigroup<Amount>>{
        concat: sum,
    },
    cheapest,
    value,
    opposite: ({ value, currency }: Amount): Amount => _Amount.build({ currency, value: -value }),
    of: (value: number, currency: Currency): Amount => Amount.build({ value, currency }),
})
